import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// CheckCircle2 and TriangleAlert belong to the hidden comparison block below.
import { Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatCurrency, formatNumber } from '@/lib/format';
import { formatStoreTime } from '@/lib/store-time';
// import { cn } from '@/lib/utils';
import { SettlementReceiptDialog } from './settlement-receipt-dialog';
import type { SettlementBatch, SettlementTender } from './settlements.api';

/**
 * One batch: what the terminal closed, what the POS recorded, and the receipt.
 *
 * The comparison comes first because it is the question a settlement report
 * exists to answer — did every charge the terminal approved make it into the
 * POS?
 */
export function SettlementSheet({
  batch,
  open,
  onOpenChange,
}: {
  batch: SettlementBatch | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [receiptOpen, setReceiptOpen] = useState(false);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
        >
          {batch && (
            <>
              <SheetHeader className="border-b pr-12">
                <SheetTitle>
                  {t('settlements.batchTitle', {
                    lane: batch.laneTag || t('settlements.laneFallback'),
                    store: batch.businessName || '—',
                  })}
                </SheetTitle>
                <SheetDescription>
                  {batch.openedAt
                    ? t('settlements.openedOn', {
                        at: formatStoreTime(batch.openedAt),
                      })
                    : t('settlements.openedUnknown')}
                  {' · '}
                  {t('settlements.closedOn', {
                    at: formatStoreTime(batch.settledAt),
                  })}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-6 p-4">
                <Comparison batch={batch} />
                <Categories batch={batch} />

                <section className="space-y-2">
                  <h3 className="text-sm font-semibold">
                    {t('settlements.terminal')}
                  </h3>
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                    <dt className="text-muted-foreground">
                      {t('settlements.terminalId')}
                    </dt>
                    <dd className="font-mono text-xs leading-5">
                      {batch.terminalId || '—'}
                    </dd>
                    <dt className="text-muted-foreground">
                      {t('settlements.batchNumber')}
                    </dt>
                    <dd className="font-mono text-xs leading-5">
                      {batch.batchNumber || '—'}
                    </dd>
                    <dt className="text-muted-foreground">
                      {t('settlements.status')}
                    </dt>
                    <dd>{batch.status || '—'}</dd>
                  </dl>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!batch.hasReceipt}
                    onClick={() => setReceiptOpen(true)}
                  >
                    <Receipt className="size-3.5" />
                    {batch.hasReceipt
                      ? t('settlements.viewReceipt')
                      : t('settlements.noReceipt')}
                  </Button>
                </section>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <SettlementReceiptDialog
        settlementId={batch?.id ?? null}
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
      />
    </>
  );
}

function Comparison({ batch }: { batch: SettlementBatch }) {
  const { t } = useTranslation();
  const matches =
    batch.compared && !batch.salesDifference && !batch.refundsDifference;

  const rows: { label: string; terminal: string; pos: string }[] = [
    {
      label: t('settlements.saleCount'),
      terminal: formatNumber(batch.saleCount),
      pos: batch.compared ? formatNumber(batch.posSaleCount) : '—',
    },
    {
      label: t('settlements.saleAmount'),
      terminal: formatCurrency(batch.sales),
      pos: batch.compared ? formatCurrency(batch.posSales) : '—',
    },
    {
      label: t('settlements.refundCount'),
      terminal: formatNumber(batch.refundCount),
      pos: batch.compared ? formatNumber(batch.posRefundCount) : '—',
    },
    {
      label: t('settlements.refundAmount'),
      terminal: formatCurrency(batch.refunds),
      pos: batch.compared ? formatCurrency(batch.posRefunds) : '—',
    },
  ];

  const notes: string[] = [];
  if (!batch.compared) {
    notes.push(t('settlements.explainNotCompared'));
  } else if (matches) {
    notes.push(t('settlements.explainMatch'));
  } else {
    if (batch.salesDifference > 0) {
      notes.push(
        t('settlements.explainTerminalHigher', {
          amount: formatCurrency(batch.salesDifference),
        })
      );
    } else if (batch.salesDifference < 0) {
      notes.push(
        t('settlements.explainPosHigher', {
          amount: formatCurrency(-batch.salesDifference),
        })
      );
    }
    if (batch.refundsDifference) {
      notes.push(
        t('settlements.explainRefunds', {
          amount: formatCurrency(Math.abs(batch.refundsDifference)),
        })
      );
    }
  }

  return (
    <section className="space-y-2">
      {/* <h3 className="text-sm font-semibold">{t('settlements.comparison')}</h3>
      <div
        className={cn(
          'flex items-start gap-2 rounded-lg border p-3 text-sm',
          !batch.compared && 'text-muted-foreground',
          matches && 'border-success/30 bg-success/8',
          batch.compared && !matches && 'border-warning/40 bg-warning/10'
        )}
      >
        {matches ? (
          <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" />
        ) : batch.compared ? (
          <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
        ) : null}
        <div className="space-y-1">
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      </div> */}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground text-xs">
            <th className="py-1 text-left font-medium" />
            <th className="py-1 text-right font-medium">
              {t('settlements.terminal')}
            </th>
            {/* <th className="py-1 text-right font-medium">
              {t('settlements.pos')}
            </th> */}
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {rows.map((row) => (
            <tr key={row.label} className="border-t">
              <td className="text-muted-foreground py-1.5">{row.label}</td>
              <td className="py-1.5 text-right">{row.terminal}</td>
              {/* <td className="py-1.5 text-right">{row.pos}</td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Categories({ batch }: { batch: SettlementBatch }) {
  const { t } = useTranslation();
  const categories: { key: string; label: string; tender: SettlementTender }[] = [
    { key: 'credit', label: t('settlements.credit'), tender: batch.credit },
    { key: 'debit', label: t('settlements.debit'), tender: batch.debit },
    { key: 'cash', label: t('settlements.cash'), tender: batch.cash },
  ];

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{t('settlements.categories')}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground text-xs">
              <th className="py-1 text-left font-medium">
                {t('settlements.category')}
              </th>
              <th className="py-1 text-right font-medium">
                {t('settlements.saleCount')}
              </th>
              <th className="py-1 text-right font-medium">
                {t('settlements.saleAmount')}
              </th>
              <th className="py-1 text-right font-medium">
                {t('settlements.refundAmount')}
              </th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {categories.map(({ key, label, tender }) => (
              <tr key={key} className="border-t">
                <td className="py-1.5">{label}</td>
                <td className="py-1.5 text-right">
                  {formatNumber(tender.saleCount)}
                </td>
                <td className="py-1.5 text-right">
                  {formatCurrency(tender.saleAmount)}
                </td>
                <td className="py-1.5 text-right">
                  {tender.refundAmount
                    ? `${formatCurrency(tender.refundAmount)} (${formatNumber(tender.refundCount)})`
                    : '—'}
                </td>
              </tr>
            ))}
            <tr className="border-t font-medium">
              <td className="py-1.5">{t('settlements.total')}</td>
              <td className="py-1.5 text-right">
                {formatNumber(batch.saleCount)}
              </td>
              <td className="py-1.5 text-right">
                {formatCurrency(batch.sales)}
              </td>
              <td className="py-1.5 text-right">
                {batch.refunds
                  ? `${formatCurrency(batch.refunds)} (${formatNumber(batch.refundCount)})`
                  : '—'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
